import React, { useEffect, useRef, useState } from "react";
import { ShieldAlert, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

// Versioned "Earnings & Verification Notice" disclosure. Shown during BR License
// application (before the license is finalized) and re-prompted when the version
// changes. Text-only by default; if a video_url is configured, the agree control
// stays locked until the viewer reaches the watch-completion threshold.
export const EarningsNotice = ({ onAck }) => {
  const [notice, setNotice] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [watchedPct, setWatchedPct] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    api.get("/compliance/earnings-notice").then((r) => {
      setNotice(r.data);
      if (r.data.acknowledged) onAck?.();
    }).catch(() => {});
  }, []); // eslint-disable-line

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (v && v.duration) setWatchedPct(Math.round((v.currentTime / v.duration) * 100));
  };

  const videoGate = notice?.video_url
    ? watchedPct >= (notice.video_watch_threshold_pct || 85)
    : true;
  const canAgree = agree && videoGate && !busy;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/compliance/earnings-notice/ack", {
        version: notice.version, video_watched_pct: watchedPct,
      });
      toast.success("Thanks - noted.");
      onAck?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  if (!notice) return <div className="p-8 text-race-sub" data-testid="earnings-notice-loading">Loading…</div>;

  return (
    <div className="max-w-lg mx-auto animate-slide-up" data-testid="earnings-notice">
      <div className="flex items-center gap-2 text-race-volt mb-2">
        <ShieldAlert size={16} /><span className="overline">One quick thing before we finish</span>
      </div>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">{notice.title}</h1>

      <div className="bg-race-surface border border-race-line rounded-sm p-5 mt-5 space-y-3">
        {notice.video_url && (
          <video ref={videoRef} src={notice.video_url} controls onTimeUpdate={onTimeUpdate}
            data-testid="earnings-notice-video" className="w-full rounded-sm bg-black" />
        )}
        {notice.paragraphs.map((p, i) => (
          <p key={i} className="text-sm text-race-txt leading-relaxed" data-testid={`earnings-notice-p-${i}`}>{p}</p>
        ))}
        {notice.video_url && !videoGate && (
          <div className="text-xs text-race-sub" data-testid="earnings-notice-video-hint">
            Please watch the video ({watchedPct}% / {notice.video_watch_threshold_pct}%) to continue.
          </div>
        )}
      </div>

      <label className="flex items-start gap-3 mt-5 cursor-pointer" data-testid="earnings-notice-agree-label">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
          data-testid="earnings-notice-agree" className="mt-1 w-4 h-4 accent-race-red" />
        <span className="text-sm text-race-txt">
          I understand and agree that I can play and refer freely, and that identity
          verification will be required before any winnings or commissions are paid out.
        </span>
      </label>

      <button onClick={submit} disabled={!canAgree} data-testid="earnings-notice-continue"
        className="w-full mt-5 bg-race-volt text-race-bg font-heading text-2xl tracking-wide py-3 rounded-sm hover:opacity-90 disabled:opacity-40 transition-opacity flex items-center justify-center gap-2">
        <Check size={20} /> {busy ? "…" : "I UNDERSTAND & AGREE"}
      </button>
    </div>
  );
};

export default EarningsNotice;
